import React, { useState, useEffect, useRef } from 'react';
import { Search, X, Check } from 'lucide-react';
import { useTheme } from '../../context/ThemeContext';
import { apiGet } from '../../utils/api';

export default function UserSearchSelect({ selectedUsers, onSelect, onRemove, excludeIds = [], context = 'group' }) {
    const { theme } = useTheme();
    const [query, setQuery] = useState('');
    const [users, setUsers] = useState([]);
    const [loading, setLoading] = useState(false);
    const [showDropdown, setShowDropdown] = useState(false);
    const inputRef = useRef(null);
    const dropdownRef = useRef(null);

    useEffect(() => {
        const fetchUsers = async () => {
            setLoading(true);
            try {
                const endpoint = context === 'block'
                    ? '/team_chat/blockable_users'
                    : `/team_chat/users?context=${encodeURIComponent(context)}`;
                const response = await apiGet(endpoint);
                if (response.ok) {
                    const data = await response.json();
                    setUsers(data.users || []);
                }
            } catch (err) {
                console.error('Failed to fetch users:', err);
            } finally {
                setLoading(false);
            }
        };
        fetchUsers();
    }, [context]);

    useEffect(() => {
        const handleClickOutside = (e) => {
            if (dropdownRef.current && !dropdownRef.current.contains(e.target) &&
                inputRef.current && !inputRef.current.contains(e.target)) {
                setShowDropdown(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    const filteredUsers = users.filter(user => {
        const isExcluded = excludeIds.includes(user.id);
        const isSelected = selectedUsers.some(u => u.id === user.id);
        if (isExcluded || isSelected) return false;

        if (!query.trim()) return true;
        const q = query.toLowerCase();
        return (
            user.username?.toLowerCase().includes(q) ||
            user.email?.toLowerCase().includes(q) ||
            user.first_name?.toLowerCase().includes(q) ||
            user.last_name?.toLowerCase().includes(q)
        );
    });

    return (
        <div className="relative">
            {/* Selected users chips */}
            {selectedUsers.length > 0 && (
                <div className="flex flex-wrap gap-1 mb-2">
                    {selectedUsers.map(user => (
                        <span
                            key={user.id}
                            className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-rasa ${
                                theme === 'dark' ? 'bg-blue-900/40 text-blue-300' : 'bg-blue-100 text-blue-700'
                            }`}
                        >
                            {user.first_name || user.username}
                            <button onClick={() => onRemove(user.id)} className="hover:opacity-70">
                                <X size={12} />
                            </button>
                        </span>
                    ))}
                </div>
            )}

            {/* Search input */}
            <div className="relative" ref={inputRef}>
                <Search size={16} className={`absolute left-3 top-1/2 -translate-y-1/2 ${
                    theme === 'dark' ? 'text-gray-400' : 'text-gray-500'
                }`} />
                <input
                    type="text"
                    value={query}
                    onChange={(e) => {
                        setQuery(e.target.value);
                        setShowDropdown(true);
                    }}
                    onFocus={() => setShowDropdown(true)}
                    placeholder="Search team members..."
                    className={`w-full pl-9 pr-4 py-2 rounded-lg text-sm font-rasa border ${
                        theme === 'dark'
                            ? 'bg-gray-700 border-gray-600 text-white placeholder-gray-400'
                            : 'bg-white border-gray-300 text-gray-900 placeholder-gray-500'
                    } focus:outline-none focus:ring-2 focus:ring-blue-500`}
                />
            </div>

            {/* Dropdown */}
            {showDropdown && (
                <div
                    ref={dropdownRef}
                    className={`absolute z-10 w-full mt-1 max-h-48 overflow-y-auto rounded-lg border shadow-lg ${
                        theme === 'dark' ? 'bg-gray-800 border-gray-600' : 'bg-white border-gray-200'
                    }`}
                >
                    {loading ? (
                        <div className="px-4 py-3 text-sm font-rasa text-center">Loading...</div>
                    ) : filteredUsers.length === 0 ? (
                        <div className={`px-4 py-3 text-sm font-rasa text-center ${
                            theme === 'dark' ? 'text-gray-400' : 'text-gray-500'
                        }`}>
                            No users found
                        </div>
                    ) : (
                        filteredUsers.map(user => (
                            <button
                                key={user.id}
                                onClick={() => {
                                    onSelect(user);
                                    setQuery('');
                                    setShowDropdown(false);
                                }}
                                className={`flex items-center gap-3 w-full px-4 py-2 text-left text-sm ${
                                    theme === 'dark' ? 'hover:bg-gray-700' : 'hover:bg-gray-50'
                                } transition-colors`}
                            >
                                {user.avatar_url ? (
                                    <img src={user.avatar_url} alt="" className="w-7 h-7 rounded-full" />
                                ) : (
                                    <div className="w-7 h-7 rounded-full bg-blue-500 flex items-center justify-center text-white text-xs">
                                        {(user.first_name || user.username)?.charAt(0)?.toUpperCase()}
                                    </div>
                                )}
                                <div className="min-w-0">
                                    <p className="truncate font-unna font-medium">
                                        {user.first_name} {user.last_name}
                                    </p>
                                    <p className={`text-xs font-rasa truncate ${
                                        theme === 'dark' ? 'text-gray-400' : 'text-gray-500'
                                    }`}>
                                        @{user.username}
                                    </p>
                                </div>
                            </button>
                        ))
                    )}
                </div>
            )}
        </div>
    );
}
